import { Component, OnInit, input, output, forwardRef } from '@angular/core';
import { inputSizeMap, InputType } from './input.config'; // Importing configuration and type definitions
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgIcon } from '@ng-icons/core';
@Component({
  selector: 'app-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  imports: [CommonModule, NgIcon],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent implements OnInit, ControlValueAccessor {
  type = input<InputType>('text'); // Input type (e.g., text, password)
  showPasswordToggle = input<boolean>(false); // Shows a visibility toggle for password inputs
  placeholder = input<string>('Custom Input'); // Placeholder text for the input
  isError = input<boolean>(false); // Indicates if the input has an error state
  isSuccess = input<boolean>(false); // Indicates if the input has a success state
  size = input<'xs' | 'sm' | 'md' | 'lg' | 'xl'>('md'); // Determines input size
  label = input<string | undefined>(undefined); // Optional label text
  iconName = input<string | undefined>(undefined); // Optional icon name
  iconColor = input<string | undefined>(undefined); // Optional icon color
  checked = input<boolean>(false); // Checked state for checkbox/radio inputs
  textAreaRows = input<number | undefined>(undefined); // Number of rows for a textarea
  textAreaCols = input<number | undefined>(undefined); // Number of columns for a textarea
  margin = input<string | undefined>(undefined); // Optional margin styling
  name = input<string>(''); // Name attribute of the input
  initialValue = input<string>(''); // Initial value of the input
  // Signal-based output using the `output()` helper for reactivity
  onChange = output<string>(); // Emits changes to the input value
  disabled = input<boolean>(false); // Disabled state of the input
  // ControlValueAccessor properties
  private _value: string = this.initialValue() || '';
  private onChangeCallback: (value: string) => void = () => {};
  private onTouchedCallback: () => void = () => {};

  // Additional internal properties
  hasIcon: boolean; // Determines if the input has an icon
  iconClass: string = 'icon'; // CSS class for the icon
  iconSize: number | undefined = undefined; // Size of the icon (calculated based on `size`)
  passwordVisible = false;

  constructor() {
    // Initialize `hasIcon` based on whether `iconName` is provided
    this.hasIcon = this.iconName != undefined;
  }

  /**
   * Emits the input value change to the `onChange` output signal.
   * @param event The DOM input event triggered by user interaction.
   */
  onInputChange = (event: any): void => {
    const value = event.target.value;
    this._value = value;
    this.onChangeCallback(value);
    this.onChange.emit(value); // Emits the updated input value
  };

  /**
   * Handles blur event to mark the control as touched
   */
  onBlur = (): void => {
    this.onTouchedCallback();
  };

  // ControlValueAccessor implementation
  writeValue(value: string | number | null | undefined): void {
    this._value = value == null ? '' : String(value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  get value(): string {
    return this._value;
  }

  get resolvedType(): InputType {
    return this.type() === 'password' && this.passwordVisible ? 'text' : this.type();
  }

  togglePasswordVisibility(): void {
    this.passwordVisible = !this.passwordVisible;
  }

  ngOnInit() {
    // Set icon class property based on the `size` input
    if (this.iconName()) {
      this.iconClass = `icon ${inputSizeMap[this.size()].iconClass}`; // Add icon size class
    }

    // Set the icon size from the icon sizes map
    this.iconSize = inputSizeMap[this.size()].iconSize;
  }
}
